import { byStopIndex } from '../../../utils/composeDriverRecords';

// Pure ordering / numbering rules shared by the Single-route map's stop discs
// and the Stops panel. Both derive from the same stops + sort mode, so the
// disc numbers and the panel rows can never disagree.

export const SORT_SCHEDULED = 'scheduled';
export const SORT_ACTUAL = 'actual';
export const SORT_MODES = [SORT_SCHEDULED, SORT_ACTUAL];

// A stop the driver has reached: stocked, or the one in flight.
const hasBeenReached = (stop) => stop.state !== 'pending';

// Visit order first; untimed / unranked stops fall back to planned order.
export function byVisitOrder(stopA, stopB) {
  const orderA = stopA.visitOrder ?? Infinity;
  const orderB = stopB.visitOrder ?? Infinity;
  if (orderA === orderB) {
    return byStopIndex(stopA, stopB);
  }
  return orderA - orderB;
}

/**
 * The route's stops in the order the given mode reads them.
 *
 * Scheduled: planned order (stop index). Actual: the stops the driver has
 * reached (stocked ones by visit order, then the one in flight), followed by
 * the not-yet-visited stops in planned order — the way a dispatcher reads
 * "what actually happened, then what's left".
 *
 * @param {Array<object>} stops routeStops (any subset).
 * @param {'scheduled'|'actual'} sortMode
 * @returns {Array<object>} A new sorted array; the input is untouched.
 */
export function orderStops(stops, sortMode) {
  if (sortMode !== SORT_ACTUAL) {
    return [...stops].sort(byStopIndex);
  }
  const reached = stops.filter(hasBeenReached).sort(byVisitOrder);
  const upcoming = stops
    .filter((stop) => !hasBeenReached(stop))
    .sort(byStopIndex);
  return [...reached, ...upcoming];
}

/**
 * The number each stop displays in the given mode, keyed by stopId.
 * Scheduled: the planned stop index. Actual: 1..n in `orderStops` order.
 *
 * @param {Array<object>} stops Pass ALL routeStops, not just the ones with
 *   coordinates, so the map and the panel number identically.
 * @param {'scheduled'|'actual'} sortMode
 * @returns {Map<string, number>}
 */
export function stopDisplayNumbers(stops, sortMode) {
  const ordered = orderStops(stops, sortMode);
  return new Map(
    ordered.map((stop, index) => [
      stop.stopId,
      sortMode === SORT_ACTUAL ? index + 1 : stop.stopIndex,
    ]),
  );
}
